import { FC, useEffect, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { MerchandisingSmallBannerComp } from '@marriott/mi-merch-components';
import { useSession } from '@marriott/mi-headless-utils';
import { getMediaDataSecondary, getMediaDataTertiary, getPropValue } from '../../utils/OfferUtils';
import { shouldUseMockData } from '../../utils/CommonUtils';
import { SECONDARY_MEDIA, TERTIARY_MEDIA } from '../../constants/CommonConstants';
import { OffersMerchandisingSmallBannerProps } from './OffersMerchandisingSmallBanner.types';
import { StyledOffersMerchandisingSmallBanner } from './OffersMerchandisingSmallBanner.styles';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

export const OffersMerchandisingSmallBannerWrapperConfig = {
  emptyLabel: 'OffersMerchandisingSmallBannerWrapper',
  isEmpty: false,
  resourceType: `mi-aem-common-spa/components/content/merchandisingsmallbanner`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OffersMerchandisingSmallBannerWrapper: FC<OffersMerchandisingSmallBannerProps> = props => {
  const { model, offersData, isAuthorMode } = props;
  const { envVarsObject } = useClientEnvVarsStore();
  const imageDomain = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mockdataInAuthorMode, setMockDataInAuthorMode] = useState<any>({});

  const { datalayer } = useSession();
  const preview_prop_currency_type = datalayer?.['prop_currency_type'] || 'USD';

  // Load mock model dynamically only for dev mode
  const GetMockData = async (isAuthorMode: boolean) => {
    if (shouldUseMockData(isAuthorMode)) {
      const mockModule = await import('./__mock__/OffersMerchandisingSmallBanner.mock');
      return mockModule.OffersMerchandisingSmallBannerMockDataWithPointsVersion;
    }
    return {};
  };

  useEffect(() => {
    const fetchMockData = async () => {
      const mockData = await GetMockData(isAuthorMode);
      setMockDataInAuthorMode(mockData);
    };

    fetchMockData();
  }, [isAuthorMode]);

  const offersDataNode = !isAuthorMode
    ? offersData?.responseObject?.edges?.[0]
    : mockdataInAuthorMode?.offersData?.responseObject?.edges?.[0];
  const merchandisingNode = offersDataNode?.merchandisingNode;
  const node = offersDataNode?.node;

  const mediaSource = isAuthorMode ? 'model' : SECONDARY_MEDIA;
  const media = isAuthorMode
    ? model?.image || node?.media
    : getMediaDataSecondary(node?.media, model?.image, imageDomain, mediaSource);
  const mathType = merchandisingNode?.mathType?.[0];

  const mediaSourceTertiary = isAuthorMode ? 'model' : TERTIARY_MEDIA;
  const tertiaryMedia = isAuthorMode
    ? { rendition: [], fileReferenceBackgroundImage: '', assetPath: '' }
    : getMediaDataTertiary(node?.media, imageDomain, mediaSourceTertiary);

  const tertiaryMediaUpdated = {
    ...tertiaryMedia,
    imageSrc: tertiaryMedia?.assetPath,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requiredProps: any = {
    openInNewTab: model?.openInNewTab,
    ctaType: model?.ctaType,
    headerLong: getPropValue(isAuthorMode, merchandisingNode?.headerLong, model?.headerLong),
    descriptionLong: getPropValue(isAuthorMode, node?.description, model?.descriptionLong),
    descriptionMedium: getPropValue(isAuthorMode, merchandisingNode?.descriptionMedium, model?.descriptionMedium),
    descriptionLongMobile: merchandisingNode?.descriptionShort,
    callToActionText1: getPropValue(isAuthorMode, node?.callToActionLabel, model?.callToActionText1),
    destinationUrl1: getPropValue(isAuthorMode, node?.callToActionLink, model?.destinationUrl1),
    mathLabel1: merchandisingNode?.mathLabel1,
    mathLabel2: merchandisingNode?.mathLabel2,
    mathLabel3: merchandisingNode?.mathLabel3,
    mathTitle: mathType,
    mathValue: merchandisingNode?.mathValue,
    legalLong: node?.termsAndConditions,
    mathType: mathType,
    image: media,
    tertiaryImage: tertiaryMediaUpdated,
    styleclass: 'standard',
    mboxParameter: model?.mboxParameter ?? false,
    displayEvergreenContent: model?.displayEvergreenContent ?? 'true',
    trackingProperties: model?.trackingProperties,
    preview_prop_currency_type: preview_prop_currency_type,
    variation: model?.variation,
    headerMedium: node?.title,
  };

  return (
    <StyledOffersMerchandisingSmallBanner
      data-testid="offers-OffersMerchandisingSmallBanner"
      data-component-name="o-offers-OffersMerchandisingSmallBanner"
    >
      <MerchandisingSmallBannerComp {...requiredProps} model={{ ...requiredProps }} />
    </StyledOffersMerchandisingSmallBanner>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OffersMerchandisingSmallBanner = (props: any) => (
  <div
    data-component-name="o-offers-OffersMerchandisingSmallBanner"
    data-testid="offers-OffersMerchandisingSmallBanner"
  >
    <EditableComponent config={OffersMerchandisingSmallBannerWrapperConfig} {...props}>
      <OffersMerchandisingSmallBannerWrapper {...props} />
    </EditableComponent>
  </div>
);
